import type { Lang } from '@contagio/engine';

/**
 * Historial de versiones: lo que la pantalla de inicio anuncia. La primera es
 * la vigente. Se escribe para quien juega, no para quien programa: que cambia
 * en la mesa, no que archivo se toco. Textos de interfaz: el espanol sin
 * tildes, y el ingles con el mismo contenido.
 */
export interface ReleaseText {
  title: string;
  notes: string[];
}

export interface Release {
  version: string;
  /** Fecha ISO (AAAA-MM-DD). */
  date: string;
  text: Record<Lang, ReleaseText>;
}

export const RELEASES: Release[] = [
  {
    version: '0.11.0',
    date: '2026-09-17',
    text: {
      es: {
        title: 'Apuestas en la mesa',
        notes: [
          'El anfitrion puede encender las apuestas en la sala y elegir cuanto pone cada persona: 10, 25 y 50 estan a un toque, y al lado se escribe la cantidad que se quiera. El bote se ve en la barra durante toda la partida y se lo lleva quien gane; si acaba en tablas, cada cual recupera lo suyo.',
          'Los bots no apuestan, asi que contra ellos no hay bote: hace falta otra persona en la mesa. Y nadie empieza una partida con una apuesta que no pueda pagar, asi que al acabar la cuenta cuadra.',
          'El bono de entrar se anuncia ahora al abrir el juego, con lo que has sumado, lo que llevas y el paquete que tienes mas cerca.',
        ],
      },
      en: {
        title: 'Bets at the table',
        notes: [
          'The host can switch bets on in the room and choose what everyone puts in: 10, 25 and 50 are one tap away, and next to them you can type any amount you like. The pot shows in the bar for the whole game and the winner takes it; if the game ends in a draw, everyone gets their stake back.',
          'Bots do not bet, so there is no pot against them: another person has to be at the table. And nobody starts a game with a bet they cannot pay, so the books balance at the end.',
          'The daily bonus now greets you when you open the game, with what you just earned, what you have and the deck you are closest to.',
        ],
      },
    },
  },
  {
    version: '0.10.0',
    date: '2026-09-17',
    text: {
      es: {
        title: 'Los paquetes se ganan jugando',
        notes: [
          'Cada partida da puntos, y da mas si en la mesa hay alguien de carne y hueso que si solo hay bots. Perder tambien suma algo, y entrar cada dia suma otros quince.',
          'Con esos puntos se abren los doce paquetes que no venian de serie, del Frutero a los dos de heroes: en la sala los ves con su candado y su precio. El paquete que elige el anfitrion lo juega la mesa entera, lo tenga o no, asi que se conocen antes de pagarlos.',
          'Sin cuentas ni contrasenas: los puntos y la coleccion se quedan en tu navegador.',
        ],
      },
      en: {
        title: 'Decks are earned by playing',
        notes: [
          'Every game pays points, and pays more when there is a real person at the table than when it is only bots. Losing pays something too, and showing up each day adds fifteen more.',
          'Those points open the twelve decks that do not come as standard, from the Fruit Bowl to the two hero decks: the room shows each one with its padlock and its price. Whatever deck the host picks is played by the whole table, owned or not, so you get to know them before paying.',
          'No accounts, no passwords: your points and your collection stay in your browser.',
        ],
      },
    },
  },
  {
    version: '0.9.0',
    date: '2026-09-16',
    text: {
      es: {
        title: 'Dema, el paquete trece',
        notes: [
          'Un mazo nuevo en la sala: reunes en tu campamento a Clancy, a Ned, a Trash el dragon y a Torchbearer, con el buitre de comodin, mientras los obispos salen de la ciudad a mancharlo todo.',
          'Trae su musica, El este es arriba, su fondo de torres y lapidas, y sus dos finales: el acantilado al amanecer, o Dema entera y Clancy con el manto de los nueve.',
          'Es un paquete de aficionado: el mundo de Dema es de Twenty One Pilots, los dibujos y la musica son propios. La nota de derechos esta en las reglas.',
        ],
      },
      en: {
        title: 'Dema, the thirteenth deck',
        notes: [
          'A new deck in the room: gather Clancy, Ned, Trash the Dragon and the Torchbearer in your camp, with the vulture as the wild card, while the bishops leave the city to smear everything.',
          'It comes with its own music, East Is Up, a backdrop of towers and gravestones, and two endings: the cliff at dawn, or the whole of Dema with Clancy in the bishops\' robe.',
          'It is a fan deck: the world of Dema belongs to Twenty One Pilots, the drawings and the music are our own. The rights note is in the rules.',
        ],
      },
    },
  },
  {
    version: '0.8.0',
    date: '2026-09-13',
    text: {
      es: {
        title: 'Cada paquete cuenta su final',
        notes: [
          'Al acabar la partida, el telon cuenta el final con una escena del paquete: si ganas, tus cuatro cartas lo celebran juntas; si pierdes, las ves por los suelos.',
          'Doce finales distintos: los heroes en su azotea, la manada al atardecer, el concierto, el naufragio... y una escena propia para las tablas.',
          'Las escenas se quedan quietas si el sistema pide movimiento reducido.',
        ],
      },
      en: {
        title: 'Every deck tells its own ending',
        notes: [
          'When a game ends, the curtain tells the ending with a scene from your deck: win and your four cards celebrate together; lose and you see them knocked down.',
          'Twelve different endings: the heroes on their rooftop, the herd at sunset, the concert, the shipwreck... and a scene of its own for a draw.',
          'Scenes stay still if your system asks for reduced motion.',
        ],
      },
    },
  },
  {
    version: '0.7.0',
    date: '2026-09-13',
    text: {
      es: {
        title: 'Otra partida, desde la sala',
        notes: [
          'Al acabar, Otra partida devuelve la mesa a la sala: ahi se cambia de paquete, de bots o de dificultad sin abrir una sala nueva, y quien quiera puede entrar con el mismo codigo.',
          'El anfitrion elige cuanto dura cada turno: 20, 30, 45 o 60 segundos. Los demas lo ven en la sala antes de empezar.',
          'Quien se va al terminar la partida deja libre su asiento para la siguiente.',
        ],
      },
      en: {
        title: 'Play again, from the room',
        notes: [
          'When a game ends, Play again takes the table back to the room: change the deck, the bots or the difficulty without opening a new room, and anyone can join with the same code.',
          'The host picks how long each turn lasts: 20, 30, 45 or 60 seconds. Everyone else sees it in the room before the game starts.',
          'Whoever leaves when a game ends frees their seat for the next one.',
        ],
      },
    },
  },
  {
    version: '0.6.0',
    date: '2026-09-11',
    text: {
      es: {
        title: 'Contagio, tambien en ingles',
        notes: [
          'Toda la interfaz, las reglas y los doce paquetes de cartas, tambien en ingles. Se cambia con el boton ES / EN y se recuerda; la primera vez manda el idioma del navegador.',
          'Cada jugador lee la mesa en su idioma: en una misma partida uno puede jugar en espanol y otro en ingles.',
          'La dificultad de los bots ahora dice Facil, Normal y Dificil, en vez de Blanda y Dura.',
        ],
      },
      en: {
        title: 'Contagio, now in English',
        notes: [
          'The whole interface, the rules and all twelve card decks, now in English too. Switch with the ES / EN button and it is remembered; the first time, your browser language decides.',
          'Each player reads the table in their own language: in the same game one can play in Spanish and another in English.',
          'Bot difficulty now reads Easy, Normal and Hard.',
        ],
      },
    },
  },
  {
    version: '0.5.1',
    date: '2026-09-11',
    text: {
      es: {
        title: 'Turnos de veinte segundos',
        notes: [
          'Cada turno dura veinte segundos en vez de un minuto. El recordatorio llega a los ocho y el reloj suena en los ultimos cinco.',
          'Los nombres largos ya no se salen de la carta en el anuncio de la jugada ni en el descarte, tampoco en el movil.',
          'El triceratops de Jurasico, redibujado de cuerpo entero: ahora se reconocen la gola y los cuernos.',
          'El panel de sonido ya no se sale de la pantalla en el movil.',
          'Los ajustes de sonido son los mismos en todas las pestanas: si silencias la musica en una, calla en las demas.',
        ],
      },
      en: {
        title: 'Twenty-second turns',
        notes: [
          'Each turn lasts twenty seconds instead of a minute. The reminder comes at eight and the clock ticks in the last five.',
          'Long names no longer spill out of the card in the move announcement or the discard pile, on mobile either.',
          'The Jurassic triceratops, redrawn full body: the frill and horns are recognizable now.',
          'The sound panel no longer runs off the screen on mobile.',
          'Sound settings are shared by every tab: mute the music in one and it stops in the others.',
        ],
      },
    },
  },
  {
    version: '0.5.0',
    date: '2026-09-11',
    text: {
      es: {
        title: 'Tu turno ya no pasa desapercibido',
        notes: [
          'Cuando te toca, una franja cruza la mesa, suena una llamada de metales y la barra se enciende con tu turno.',
          'Si pasan veinte segundos sin que juegues, la mesa te lo recuerda. La pestana del navegador avisa aunque estes en otra.',
          'Musica de fondo para cada paquete: marcha de metales para Heroes DC, himno para Heroes Marvel, jiga para Piratas y una pieza propia para cada uno de los demas.',
          'El boton de sonido abre un panel con el volumen de la musica y el de los efectos por separado.',
          'Esta lista de novedades, para ir contando lo que cambia.',
        ],
      },
      en: {
        title: 'Your turn no longer goes unnoticed',
        notes: [
          'When it is your turn, a band sweeps across the table, a brass call sounds and the top bar lights up.',
          'If twenty seconds pass without a move, the table reminds you. The browser tab tells you too, even from another tab.',
          'Background music for every deck: a brass march for DC Heroes, an anthem for Marvel Heroes, a jig for Pirates and a piece of its own for each of the rest.',
          'The sound button opens a panel with separate volumes for music and effects.',
          'This list of what\'s new, to keep track of what changes.',
        ],
      },
    },
  },
  {
    version: '0.4.0',
    date: '2026-09-11',
    text: {
      es: {
        title: 'La mesa suena',
        notes: [
          'Sonidos para barajar y repartir, para cada tipo de jugada, para los ultimos diez segundos del turno y para el final.',
          'Los rivales se sientan en el orden en que juegan, vistos desde tu silla.',
          'Cada turno estrena reloj, aunque jueguen dos personas seguidas.',
        ],
      },
      en: {
        title: 'The table has sound',
        notes: [
          'Sounds for shuffling and dealing, for each kind of move, for the last ten seconds of the turn and for the end.',
          'Rivals sit in the order they play, as seen from your chair.',
          'Every turn starts a fresh clock, even when two people play in a row.',
        ],
      },
    },
  },
  {
    version: '0.3.0',
    date: '2026-09-10',
    text: {
      es: {
        title: 'Doce paquetes de cartas',
        notes: [
          'El anfitrion elige el mazo en la sala: Contagio, Heroes DC, Heroes Marvel, Frutero, Cortafuegos, Orbita, Asedio, Arrecife, Grimorio, Jurasico, Banda o Piratas.',
          'Cambian los nombres, los dibujos y el fondo; las reglas son las mismas.',
          'Contagio se puede jugar en contagio.emiigg.dev.',
        ],
      },
      en: {
        title: 'Twelve card decks',
        notes: [
          'The host picks the deck in the room: Contagio, DC Heroes, Marvel Heroes, Fruit Bowl, Firewall, Orbit, Siege, Reef, Grimoire, Jurassic, The Band or Pirates.',
          'Names, drawings and background change; the rules stay the same.',
          'Contagio can be played at contagio.emiigg.dev.',
        ],
      },
    },
  },
  {
    version: '0.2.0',
    date: '2026-09-09',
    text: {
      es: {
        title: 'Tema oscuro y un minuto por turno',
        notes: [
          'Quien abre la partida sale por sorteo.',
          'Tema claro, oscuro o el del sistema, y el instrumental de hospital de fondo.',
          'Un minuto por turno: si se agota, la mesa juega por ti.',
          'Los organos se colocan pinchando su hueco, y la negligencia medica pide confirmacion.',
          'Con cuatro rivales o mas, tambien se sientan a los costados. La mesa cabe en un movil.',
        ],
      },
      en: {
        title: 'Dark theme and one minute per turn',
        notes: [
          'The first player is drawn at random.',
          'Light, dark or system theme, and hospital instruments in the background.',
          'One minute per turn: if it runs out, the table plays for you.',
          'Organs are placed by clicking their slot, and medical malpractice asks for confirmation.',
          'With four rivals or more, players also sit at the sides. The table fits on a phone.',
        ],
      },
    },
  },
  {
    version: '0.1.0',
    date: '2026-09-09',
    text: {
      es: {
        title: 'Primera mesa',
        notes: [
          'Partidas de dos a seis jugadores en tiempo real, con bots para completar la mesa.',
          'Reparto a la vista y cada jugada anunciada en el centro de la mesa.',
          'Si se corta la conexion, vuelves a tu asiento.',
        ],
      },
      en: {
        title: 'First table',
        notes: [
          'Real-time games for two to six players, with bots to fill the table.',
          'Cards dealt in plain sight and every move announced in the middle of the table.',
          'If your connection drops, you get your seat back.',
        ],
      },
    },
  },
];

export const CURRENT: Release = RELEASES[0]!;

const MONTHS: Record<Lang, string[]> = {
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

/** "11 sep 2026" en espanol, "Sep 11, 2026" en ingles. */
export function releaseDate(iso: string, lang: Lang): string {
  const [year, month, day] = iso.split('-').map(Number);
  const name = MONTHS[lang][(month ?? 1) - 1];
  return lang === 'en' ? `${name} ${day}, ${year}` : `${day} ${name} ${year}`;
}

const SEEN_KEY = 'contagio.release';

/** La etiqueta de "nuevo" dura hasta que se abre el historial con esta version ya publicada. */
export function hasUnseenRelease(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) !== CURRENT.version;
  } catch {
    return false;
  }
}

export function markReleaseSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, CURRENT.version);
  } catch {
    /* modo privado: volvera a salir como nueva */
  }
}
